'use client'
import { useEffect } from 'react'
import { toast } from 'sonner'
import { Sparkles, RefreshCw } from 'lucide-react'
import { useT } from '@/lib/i18n'

/**
 * Registers the service worker, asks it to look for a new version on a
 * schedule, and offers the reload that makes one visible.
 *
 * Two things were missing, and together they are why a deploy took the owner
 * two closes of the app to see (2026-09-12):
 *
 *   1. Nothing ever called `update()`. The browser checks for a new worker on
 *      its own terms — roughly on a navigation, and an installed PWA reopened
 *      from the home screen may not make one for a long time.
 *   2. Navigations are served CACHE-FIRST (deliberately: it is what lets a
 *      till boot with no connection). So even when a new worker installs, the
 *      launch that installed it still renders the shell that was already
 *      cached. The new one appears on the NEXT launch.
 *
 * So: check every 30 minutes and whenever the window comes back to the
 * foreground, and when a new worker takes over, OFFER a reload rather than
 * take one. A till that reloads itself mid-sale loses the cart — the toast
 * waits as long as the cashier needs.
 */
const UPDATE_EVERY_MS = 30 * 60 * 1000
/** A foreground check no more often than this — switching apps at a till is
 *  constant, and each check is a network request. */
const MIN_GAP_MS = 5 * 60 * 1000

/**
 * The card itself.
 *
 * The first version was a default toast: a title string and an action button,
 * which renders as a pale slab with a dark pill on it — the owner asked for
 * something with more care in it, and he was right that this one moment
 * deserves it. It is the only time the app ever speaks about itself.
 *
 * So: the shop's own accent, one icon tile, a line that says what the button
 * will do, and a way to say "not now" — because the cashier who sees this
 * mid-sale needs to dismiss it without thinking. Solid colour, no gradient,
 * no second accent, and it reads in both themes.
 */
function UpdateCard({ onReload, onLater }: { onReload: () => void; onLater: () => void }) {
  const t = useT()
  return (
    <div className="w-[min(22rem,calc(100vw-2rem))] rounded-2xl border border-border bg-popover text-popover-foreground shadow-[0_20px_48px_-16px_oklch(0.25_0.03_255/0.28),0_4px_12px_-4px_oklch(0.25_0.03_255/0.08)] p-3.5 flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <span className="relative flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          {/* A quiet ring rather than a spinner: nothing is loading, and a
              spinner at a till reads as "wait for me". */}
          <span className="absolute inset-0 rounded-xl ring-1 ring-primary/20" />
          <Sparkles className="size-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-black leading-snug">{t('pwa.ready')}</p>
          <p className="mt-0.5 text-[12px] text-muted-foreground leading-snug">
            {t('pwa.body')}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onLater}
          className="h-9 px-3 rounded-xl text-[12px] font-bold text-muted-foreground hover:bg-muted transition-colors"
        >
          {t('pwa.later')}
        </button>
        <button
          type="button"
          onClick={onReload}
          className="press ml-auto h-9 px-4 rounded-xl bg-primary text-primary-foreground text-[12px] font-black inline-flex items-center gap-1.5 hover:bg-primary/90 transition-colors"
        >
          <RefreshCw className="size-3.5" /> {t('pwa.reload')}
        </button>
      </div>
    </div>
  )
}

export function PwaRegister() {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    // A controller already present means an UPDATE is possible from here on.
    // Without this guard the very first install — where `controllerchange`
    // also fires — would greet a new user with "a new version is ready".
    const hadController = !!navigator.serviceWorker.controller
    let disposed = false
    let last = Date.now()
    let timer: ReturnType<typeof setInterval> | undefined

    const onControllerChange = () => {
      if (disposed || !hadController) return
      toast.custom(
        (id) => (
          <UpdateCard
            onReload={() => location.reload()}
            onLater={() => toast.dismiss(id)}
          />
        ),
        { duration: Infinity },
      )
    }
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)

    let onVisible: (() => void) | undefined
    navigator.serviceWorker.register('/sw.js').then((reg) => {
      if (disposed) return
      const check = () => {
        last = Date.now()
        reg.update().catch(() => {})
      }
      timer = setInterval(check, UPDATE_EVERY_MS)
      onVisible = () => {
        if (document.visibilityState === 'visible' && Date.now() - last > MIN_GAP_MS) check()
      }
      document.addEventListener('visibilitychange', onVisible)
    }).catch(() => {})

    return () => {
      disposed = true
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
      if (timer) clearInterval(timer)
      if (onVisible) document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])
  return null
}
